import { useEffect, useReducer, useState } from 'react'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { getOnboardingStatusCached } from 'entities/WebMcp/lib/onboardingStatus'
import { type OnboardingStatus } from 'entities/WebMcp/lib/onboardingSignals'
import { isOnboardingSkipped } from 'entities/WebMcp/lib/onboardingSkipState'
import { onAgentStateChanged } from 'entities/WebMcp/lib/agentStateSignal'
import { selectEmptyState } from 'features/AiAgent/model/emptyState'

/**
 * Whether the agent has something proactive waiting — an incomplete onboarding
 * step. Drives the launcher's "something for you" indicator. `area` is not
 * needed (only the onboarding tier matters), so it is passed as null.
 * `active` must be false until the flag is on and the user is logged in — the
 * onboarding read fans out to authenticated APIs and its result is cached for
 * the session, so an unauthenticated fetch would poison the cache.
 */
export const useAgentBadge = (active: boolean): boolean => {
  const { usersStore } = useEntitiesContext()
  const userId = usersStore.user?.id
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null>(null)
  const [, recompute] = useReducer((n: number) => n + 1, 0)

  useEffect(() => {
    if (!active) return undefined
    let cancelled = false
    getOnboardingStatusCached()
      .then((result) => !cancelled && setOnboarding(result))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [active])

  // Recompute when the panel marks onboarding skipped so the badge clears.
  useEffect(() => onAgentStateChanged(recompute), [])

  if (!active || !onboarding) return false

  const { tier } = selectEmptyState({
    area: null,
    onboarding,
    role: usersStore.user?.role,
    onboardingSkipped: isOnboardingSkipped(userId),
  })
  return tier === 'onboarding'
}
